// AreaChart.tsx — Atrium performance area chart (Sprint 83, ADR-082).
// Recharts (^3.7.0, already a dependency) replaces the prototype's hand-rolled
// SVG. Smooth ~2.6px line, gradient fill (color→transparent), 3 gridlines,
// white-filled dots, x-axis labels. Height ~210. Color varies by role.
import React, { useId } from 'react';
import {
  AreaChart as RAreaChart,
  Area,
  XAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

export interface AtriumAreaChartProps {
  /** Y values, one per x label. */
  points: number[];
  /** X-axis labels (same length as points). */
  labels: string[];
  /** Series color (CSS color or token-resolved hex). Defaults to brand blue. */
  color?: string;
  /** Chart height in px. */
  height?: number;
  /** Optional value formatter for the tooltip. */
  valueFormatter?: (v: number) => string;
  /**
   * Accessible text alternative (WCAG 1.1.1). The Recharts `<svg>` is decorative
   * to AT; this string is exposed via role="img"/aria-label on the wrapper plus a
   * visually-hidden data table so screen-reader users get the series in full.
   */
  ariaLabel?: string;
}

export const AtriumAreaChart: React.FC<AtriumAreaChartProps> = ({
  points,
  labels,
  color = '#2563EB',
  height = 210,
  valueFormatter,
  ariaLabel,
}) => {
  const gid = `atrium-area-${useId().replace(/:/g, '')}`;
  const data = points.map((v, i) => ({ x: labels[i] ?? String(i), v }));
  const fmt = valueFormatter ?? ((v: number) => String(v));

  return (
    <div
      className="chartwrap"
      style={{ height }}
      role="img"
      aria-label={ariaLabel ?? 'Performance area chart'}
    >
      {/* Visually-hidden data table — the full text alternative AT reads (1.1.1). */}
      <table style={{
        position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
        overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
      }}>
        <caption>{ariaLabel ?? 'Performance over time'}</caption>
        <thead><tr><th scope="col">Period</th><th scope="col">Value</th></tr></thead>
        <tbody>
          {data.map((d, i) => (
            <tr key={i}><th scope="row">{d.x || `Point ${i + 1}`}</th><td>{fmt(d.v)}</td></tr>
          ))}
        </tbody>
      </table>
      {/* Decorative svg — hidden from AT since the table above carries the data. */}
      <div aria-hidden="true" style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <RAreaChart data={data} margin={{ top: 10, right: 6, left: 6, bottom: 0 }}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.18} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* three horizontal gridlines, no vertical */}
          <CartesianGrid vertical={false} stroke="var(--line)" strokeWidth={1} />
          <XAxis
            dataKey="x"
            tickLine={false}
            axisLine={false}
            // Sprint 116 (116.2) — axis labels carry the month scale; bump from
            // --mut2 (#6A7180, dips below 4.5:1 on tinted cards) to the darker
            // --ink2 (#37425C, ~8:1) and a slightly larger size for AA legibility.
            tick={{ fontSize: 11.5, fontWeight: 600, fill: 'var(--ink2, #37425C)' }}
            dy={6}
          />
          <Tooltip
            cursor={{ stroke: color, strokeOpacity: 0.3 }}
            formatter={(value) =>
              valueFormatter ? valueFormatter(Number(value)) : String(value)
            }
            contentStyle={{
              borderRadius: 10,
              border: '1px solid var(--line)',
              boxShadow: 'var(--sh2)',
              fontSize: 12,
            }}
          />
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={2.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill={`url(#${gid})`}
            dot={{ r: 3.2, fill: '#fff', stroke: color, strokeWidth: 2 }}
            activeDot={{ r: 4.5, fill: '#fff', stroke: color, strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </RAreaChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AtriumAreaChart;
